import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import begnerLogo from "../assets/begner-logo-256.webp";
import interTightFont from "../assets/fonts/inter-tight-latin.woff2";
import manropeFont from "../assets/fonts/manrope-latin.woff2";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "../components/site-header";
import { SiteFooter } from "../components/site-footer";
import { RouteToaster } from "../components/route-toaster";
import { LanguageProvider } from "../lib/i18n";
import { MetaPixel } from "../components/meta-pixel";
import { jsonLdScript, organizationLd, websiteLd } from "../lib/json-ld";
import { siteInfo } from "../lib/site-info";

const cookieBannerBaseUrl =
  "https://legally-snippet.legal-cdn.com/v1/public/websites/H8gKsDLMNL8K4Ddp3SQwsCOxcW8Uhl/cookie-banner/latest";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Begner. Der einfachste Weg zu guten Handwerkern" },
      {
        name: "description",
        content:
          "Begner verbindet Sie mit geprüften Handwerkern und Auftragnehmern in der Schweiz, mit echten Kundenbewertungen.",
      },
      { name: "author", content: "Begner" },
      { property: "og:locale", content: "de_CH" },
      { property: "og:site_name", content: "Begner" },
      { name: "geo.region", content: "CH" },
      { name: "geo.placename", content: "Schweiz" },
      { name: "google-site-verification", content: "8uSM4bapcDDViLVJqGKwgXUKU7dKAlXKxEEvozqn2EY" },
      { property: "og:title", content: "Begner. Der einfachste Weg zu guten Handwerkern" },
      {
        property: "og:description",
        content:
          "Geprüfte Handwerker und Bauunternehmen in der Schweiz finden, mit echten Kundenbewertungen.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Begner. Der einfachste Weg zu guten Handwerkern" },
      { name: "twitter:description", content: "Finden Sie geprüfte Handwerker für Ihr Projekt." },
      { property: "og:image", content: `${siteInfo.url}${siteInfo.socialImagePath}` },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Begner Logo" },
      { name: "twitter:image", content: `${siteInfo.url}${siteInfo.socialImagePath}` },
      { name: "twitter:image:alt", content: "Begner Logo" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "alternate", type: "text/plain", title: "LLM summary", href: "/llms.txt" },
      { rel: "icon", type: "image/webp", href: begnerLogo },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      // Keep larger-screen text stable without competing with the mobile LCP image.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
        media: "(min-width: 640px)",
        href: interTightFont,
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
        media: "(min-width: 640px)",
        href: manropeFont,
      },
    ],
    scripts: [jsonLdScript(organizationLd), jsonLdScript(websiteLd)],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function CanonicalLink() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const clean = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
  const href = `https://begner.ch${clean}`;
  return (
    <>
      <link rel="canonical" href={href} />
      <link rel="alternate" hrefLang="de-CH" href={href} />
      <link rel="alternate" hrefLang="x-default" href={href} />
    </>
  );
}

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="de-CH">
      <head>
        <CanonicalLink />
        <HeadContent />
        <link rel="preconnect" href="https://legally-snippet.legal-cdn.com" />
        {/* Keep classic defer scripts in order. MetaPixel also requires explicit
            marketing consent, including while the consent scripts are loading. */}
        <script type="text/javascript" src={`${cookieBannerBaseUrl}/blocker.js`} defer />
        <script type="text/javascript" src={`${cookieBannerBaseUrl}/banner.js`} defer />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <LanguageProvider>
      <MetaPixel />
      <div className="flex min-h-screen flex-col bg-background">
        <SiteHeader />
        <main className="flex-1">
          <Outlet />
        </main>
        <SiteFooter />
      </div>
      <RouteToaster />
    </LanguageProvider>
  );
}
